import { find, last, mapValues, pickBy, uniq } from "lodash"

import { CLASS_GROUPS } from "#constants/classOptions"
import {
  ALL_SKILL_LEVELS,
  type AllSkillLevels,
  CLASS_GROUP_TO_SKILL_TYPE,
  getStudentSkill,
} from "#constants/skillLevelConstants"
import {
  EVAL_STATUS_SORT_ORDER,
  EVALUATION_STATUS,
  type EvaluationConstructor,
  type EvaluationTrait,
} from "#customTypes/composedTypes/evaluationTypes"
import { type StudentConstructor } from "#customTypes/composedTypes/studentTypes"
import { type UserConstructor } from "#customTypes/composedTypes/userTypes"
import { type Uuid } from "#customTypes/globalTypes"
import { generateEvalId } from "#utils/evaluationHelpers"

export type ClassStudent = {
  id: Uuid
  studentId: Uuid
  classId: Uuid
  evaluationId: Uuid
}

export const getCurrentSkillForStudent = (
  student: Pick<StudentConstructor, "skillHistory"> | undefined,
  classGroup: CLASS_GROUPS,
  semester: string
): AllSkillLevels | "" =>
  getStudentSkill(
    student?.skillHistory,
    CLASS_GROUP_TO_SKILL_TYPE[classGroup],
    semester
  )

export const getSortedStudentIds = (
  studentIds: Uuid[],
  evaluationsById: Record<string, Pick<EvaluationConstructor, "status">>,
  studentsById: Record<string, Pick<StudentConstructor, "name">>,
  semester: string,
  classGroup: CLASS_GROUPS,
  sortByStatus: boolean
): Uuid[] =>
  uniq(studentIds).sort((idA, idB) => {
    if (sortByStatus) {
      const evalA = evaluationsById[generateEvalId(idA, semester, classGroup)]
      const evalB = evaluationsById[generateEvalId(idB, semester, classGroup)]

      const orderA = EVAL_STATUS_SORT_ORDER.indexOf(
        evalA?.status ?? EVALUATION_STATUS.NOT_STARTED
      )
      const orderB = EVAL_STATUS_SORT_ORDER.indexOf(
        evalB?.status ?? EVALUATION_STATUS.NOT_STARTED
      )

      if (orderA !== orderB) return orderA - orderB
    }

    return studentsById[idA]?.name?.localeCompare(studentsById[idB]?.name) ?? 0
  })

export const getVisibleStudentIds = (
  sortedStudentIds: Uuid[],
  filteredStudentIds: Uuid[] | undefined,
  searchTerm: string,
  className: string,
  teacherIds: Uuid[],
  usersById: Record<string, Pick<UserConstructor, "name">>,
  studentsById: Record<string, Pick<StudentConstructor, "name">>
): Uuid[] => {
  let ids = sortedStudentIds

  if (filteredStudentIds) {
    const allowedSet = new Set(filteredStudentIds)
    ids = ids.filter((id) => allowedSet.has(id))
  }

  if (searchTerm) {
    const term = searchTerm.toLowerCase()
    const classNameMatches = className.toLowerCase().includes(term)
    const teacherMatches = teacherIds.some((tid) =>
      usersById[tid]?.name?.toLowerCase().includes(term)
    )
    if (!classNameMatches && !teacherMatches) {
      ids = ids.filter((id) =>
        studentsById[id]?.name?.toLowerCase().includes(term)
      )
    }
  }

  return ids
}

export const getMissingSkillStudentIds = (
  targetStudentIds: Uuid[],
  evaluationsById: Record<string, unknown>,
  studentsById: Record<string, Pick<StudentConstructor, "skillHistory">>,
  semester: string,
  classGroup: CLASS_GROUPS
): Uuid[] =>
  targetStudentIds.filter((studentId) => {
    const evalId = generateEvalId(studentId, semester, classGroup)
    return (
      !evaluationsById[evalId] &&
      !getCurrentSkillForStudent(studentsById[studentId], classGroup, semester)
    )
  })

export const buildStudentRows = (
  visibleStudentIds: Uuid[],
  classId: Uuid,
  semester: string,
  classGroup: CLASS_GROUPS
): ClassStudent[] =>
  visibleStudentIds.map((studentId) => ({
    id: `${studentId}-${classId}`,
    studentId,
    classId,
    evaluationId: generateEvalId(studentId, semester, classGroup),
  }))

export const getMaxSkillLevelWidth = (classGroup: CLASS_GROUPS): number => {
  const skillLevels = ALL_SKILL_LEVELS[classGroup]
  if (!skillLevels) return 0
  const skillLevelStrings = Object.keys(skillLevels)
  const maxLength = Math.max(
    ...skillLevelStrings.map((level) => level.replace("^", "").length)
  )
  return maxLength * 10 + 12 + 44
}

export const getNextSkillLevel = (
  currentSkill: string,
  classGroup: CLASS_GROUPS
): string => {
  const skillLevels = Object.keys(ALL_SKILL_LEVELS[classGroup])
  const nextSkillIndex =
    skillLevels.findIndex((skill) => skill === currentSkill) + 1
  return nextSkillIndex === skillLevels.length
    ? (last(skillLevels) ?? currentSkill)
    : skillLevels[nextSkillIndex]
}

export const buildEvalCheckboxes = (
  evalTraitsById: Record<Uuid, EvaluationTrait>,
  currentSkill: string,
  prevEval: Pick<EvaluationConstructor, "checkboxes"> | null,
  prevEvalTraitsById: Record<Uuid, EvaluationTrait>
): Record<Uuid, boolean> => {
  const currentSkillBase = currentSkill.replace("^", "")
  const matchingTraits = pickBy(evalTraitsById, (trait) =>
    trait.skillLevels.includes(currentSkillBase)
  )
  return mapValues(matchingTraits, ({ name }) => {
    const previousCheckbox = find(prevEvalTraitsById, { name })
    if (!previousCheckbox || !prevEval) return false
    return prevEval.checkboxes[previousCheckbox.id]
  })
}

/** Assigned teacher, or anyone who can assign teachers. */
export const canEditPromotion = (
  currentUserId: string | null | undefined,
  assignedTo: string | null | undefined,
  canAssignTeacher: boolean
): boolean =>
  canAssignTeacher || (!!currentUserId && currentUserId === assignedTo)

/** Lead (assign permission), or the approver saved on this evaluation. */
export const canApprovePromotion = (
  currentUserId: string | null | undefined,
  approverId: string | null | undefined,
  canAssignTeacher: boolean
): boolean =>
  canAssignTeacher || (!!currentUserId && currentUserId === approverId)

/** Assignment does not change status. NO_EVAL stays NO_EVAL. */
export const getStatusAfterTeacherAssignment = (
  currentStatus: EVALUATION_STATUS
): EVALUATION_STATUS => currentStatus
